import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, MoreHorizontal, Pencil, Printer, Trash2 } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { HubPageTitle } from "@/components/HubPageTitle";
import { Button } from "@/components/ui/Button";
import { SaveChangesButton } from "@/components/ui/SaveChangesButton";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { formSnapshotDirty } from "@/lib/formDirty";
import { insideSalesQuoteRestricted, useIntegratorMe } from "@/lib/me-query";
import { computeHorizontalMenuPanel, FLOATING_POPOVER_Z_INDEX, type AnchoredMenuPanelBox } from "@/lib/viewportAnchoredPopover";
import { cn } from "@/lib/utils";

import {
  createQuote,
  deleteQuote,
  downloadQuotePdf,
  printQuotePdf,
  duplicateQuote,
  fetchQuote,
  fetchQuoteClientOptions,
  fetchQuoteEndUserOptions,
  fetchQuotePricing,
  fetchQuoteEndUserOrders,
  fetchQuoteEndUserQuotes,
  fetchQuoteSettings,
  fetchQuoteSkuOptions,
  INSIDE_SALES_QUOTES_QUERY_KEY,
  updateQuote,
  type QuoteDetail,
  type QuoteLine,
  type QuotePricingResponse,
  type QuoteWriteInput,
} from "./insideSalesQuotesApi";
import {
  addDecimalStrings,
  decimalStringGreaterThan,
  formatMoneyString,
  multiplyDecimalStrings,
} from "./quoteDecimal";
import {
  fetchInsideSalesApproval,
  linkInsideSalesApproval,
} from "./insideSalesApprovalsApi";
import {
  defaultQuoteComments,
  defaultQuoteValidThrough,
  QUOTE_PAYMENT_TERMS,
} from "./quoteDefaults";
import {
  QUOTE_CLIENT_TYPES,
  quoteProductPricingTiers,
  quoteProductTierRange,
  type QuoteProductPricingTier,
} from "./quoteProductPricing";
import { restrictedQuoteCopyBlockReason, restrictedQuoteLineIssues } from "./quoteLineGuardrails";

type DraftLine = QuoteLine & { localId: string; warnings: string[]; blockingError: string | null };

function daysUntilDate(dateText: string): number | null {
  const parts = dateText.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) return null;
  const now = new Date();
  const todayUtc = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const targetUtc = Date.UTC(parts[0]!, parts[1]! - 1, parts[2]!);
  return Math.round((targetUtc - todayUtc) / 86_400_000);
}

function formatHistoryDate(value: string | null | undefined): string {
  const normalized = value?.slice(0, 10) ?? "";
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return "-";
  return `${month}/${day}/${year}`;
}

function emptyLine(): DraftLine {
  return {
    localId: crypto.randomUUID(),
    sku: "",
    description: "",
    quantity: "1",
    unitPrice: "",
    pricingType: "",
    originalPrice: null,
    minimumQuantity: null,
    maximumQuantity: null,
    warnings: [],
    blockingError: null,
  };
}

function detailLines(detail: QuoteDetail): DraftLine[] {
  return detail.lines.map((line) => ({
    ...line,
    localId: line.id ?? crypto.randomUUID(),
    warnings: [],
    blockingError: null,
  }));
}

type QuoteEditorRestore = {
  clientId: string;
  endUserId: string;
  validThrough: string;
  paymentTerm: string;
  comments: string;
  commentsManuallyEdited: boolean;
  lines: DraftLine[];
};

function cloneDraftLines(lines: DraftLine[]): DraftLine[] {
  return lines.map((line) => ({ ...line, warnings: [...line.warnings] }));
}

function quoteEditorPersistSnapshot(input: {
  clientId: string;
  endUserId: string;
  validThrough: string;
  paymentTerm: string;
  comments: string;
  lines: DraftLine[];
}) {
  return {
    clientId: input.clientId,
    endUserId: input.endUserId,
    validThrough: input.validThrough,
    paymentTerm: input.paymentTerm,
    comments: input.comments,
    lines: input.lines.map((line) => ({
      sku: line.sku,
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      pricingType: line.pricingType,
    })),
  };
}

export function InsideSalesQuoteEditorPage() {
  const { quoteId } = useParams<{ quoteId: string }>();
  const [searchParams] = useSearchParams();
  const approvalRequestId = searchParams.get("approvalRequestId")?.trim() || null;
  const isEdit = Boolean(quoteId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const meQuery = useIntegratorMe();
  const quoteRestricted = insideSalesQuoteRestricted(meQuery.data);
  const quotePrivileged = !quoteRestricted;
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [clientId, setClientId] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [endUserId, setEndUserId] = useState("");
  const [endUserSearch, setEndUserSearch] = useState("");
  const [validThrough, setValidThrough] = useState(defaultQuoteValidThrough);
  const [paymentTerm, setPaymentTerm] = useState("Net 30");
  const [comments, setComments] = useState("");
  const [commentsManuallyEdited, setCommentsManuallyEdited] = useState(false);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editingLineSnapshot, setEditingLineSnapshot] = useState<DraftLine | null>(null);
  const [productSku, setProductSku] = useState("");
  const [productSkuSearch, setProductSkuSearch] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const [productDescriptionError, setProductDescriptionError] = useState("");
  const [productPricingType, setProductPricingType] = useState("standard");
  const [productTiers, setProductTiers] = useState<QuoteProductPricingTier[]>([]);
  const [productQuantities, setProductQuantities] = useState<Record<number, string>>({});
  const [productPrices, setProductPrices] = useState<Record<number, string>>({});
  const [pricingDiagnostics, setPricingDiagnostics] = useState<QuotePricingResponse["diagnostics"]>();
  const [pricingLogOpen, setPricingLogOpen] = useState(false);
  const [customProductOpen, setCustomProductOpen] = useState(false);
  const customProductMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const [customProductMenuOpen, setCustomProductMenuOpen] = useState(false);
  const [customProductMenuBox, setCustomProductMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const [customSku, setCustomSku] = useState("");
  const [customDescription, setCustomDescription] = useState("");
  const [customQuantity, setCustomQuantity] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [appliedApprovalRequestId, setAppliedApprovalRequestId] = useState<string | null>(null);
  const [quoteBaseline, setQuoteBaseline] = useState<ReturnType<typeof quoteEditorPersistSnapshot> | null>(null);
  const [quoteRestore, setQuoteRestore] = useState<QuoteEditorRestore | null>(null);
  const [endUserHistoryTab, setEndUserHistoryTab] = useState<"quotes" | "orders">("quotes");

  const quoteQuery = useQuery({
    queryKey: ["inside-sales", "quotes", quoteId],
    queryFn: () => fetchQuote(quoteId ?? ""),
    enabled: isEdit,
  });
  const clientsQuery = useQuery({
    queryKey: ["inside-sales", "quotes", "options", "clients"],
    queryFn: fetchQuoteClientOptions,
  });
  const endUsersQuery = useQuery({
    queryKey: ["inside-sales", "quotes", "options", "end-users"],
    queryFn: fetchQuoteEndUserOptions,
  });
  const approvalRequestQuery = useQuery({
    queryKey: ["inside-sales", "approvals", approvalRequestId],
    queryFn: () => fetchInsideSalesApproval(approvalRequestId ?? ""),
    enabled: !isEdit && approvalRequestId != null,
  });
  const skusQuery = useQuery({
    queryKey: ["inside-sales", "quotes", "options", "skus"],
    queryFn: fetchQuoteSkuOptions,
  });
  const quoteSettingsQuery = useQuery({
    queryKey: ["inside-sales", "quotes", "settings"],
    queryFn: fetchQuoteSettings,
  });
  const endUserQuotesQuery = useQuery({
    queryKey: ["inside-sales", "quotes", "end-user-quotes", endUserId],
    queryFn: () => fetchQuoteEndUserQuotes(endUserId),
    enabled: Boolean(endUserId),
  });
  const endUserOrdersQuery = useQuery({
    queryKey: ["inside-sales", "quotes", "end-user-orders", endUserId],
    queryFn: () => fetchQuoteEndUserOrders(endUserId),
    enabled: Boolean(endUserId),
  });

  useEffect(() => {
    const quote = quoteQuery.data;
    if (!quote || loadedId === quote.id) return;
    const loadedLines = detailLines(quote);
    setClientId(quote.clientId ?? "");
    setEndUserId(quote.endUserId ?? "");
    setValidThrough(quote.validThrough?.slice(0, 10) ?? "");
    setPaymentTerm(quote.paymentTerm ?? "");
    setComments(quote.comments ?? "");
    setCommentsManuallyEdited(Boolean(quote.comments));
    setLines(loadedLines);
    const comments = quote.comments || defaultQuoteComments(loadedLines);
    setQuoteBaseline(
      quoteEditorPersistSnapshot({
        clientId: quote.clientId ?? "",
        endUserId: quote.endUserId ?? "",
        validThrough: quote.validThrough?.slice(0, 10) ?? "",
        paymentTerm: quote.paymentTerm ?? "",
        comments,
        lines: loadedLines,
      }),
    );
    setQuoteRestore({
      clientId: quote.clientId ?? "",
      endUserId: quote.endUserId ?? "",
      validThrough: quote.validThrough?.slice(0, 10) ?? "",
      paymentTerm: quote.paymentTerm ?? "",
      comments,
      commentsManuallyEdited: Boolean(quote.comments),
      lines: cloneDraftLines(loadedLines),
    });
    setLoadedId(quote.id);
  }, [loadedId, quoteQuery.data]);

  useEffect(() => {
    const approval = approvalRequestQuery.data;
    if (
      isEdit ||
      approvalRequestId == null ||
      approval?.aiStatus !== "ready" ||
      appliedApprovalRequestId === approvalRequestId
    ) return;
    const payload = approval.aiPayload;
    setClientId(payload?.clientId ?? "");
    setEndUserId(payload?.endUserId ?? "");
    setPaymentTerm(payload?.paymentTerm ?? "Net 30");
    setComments(payload?.comments ?? "");
    setCommentsManuallyEdited(Boolean(payload?.comments));
    if (payload?.lines && payload.lines.length > 0) {
      setLines(payload.lines.map((line) => ({
        ...emptyLine(),
        sku: line.sku?.trim() ?? "",
        description: line.description?.trim() ?? "",
        quantity: line.quantity == null ? "1" : String(line.quantity),
        unitPrice: line.unitPrice == null ? "" : String(line.unitPrice),
        warnings: payload.warnings ?? [],
      })));
    }
    setAppliedApprovalRequestId(approvalRequestId);
  }, [appliedApprovalRequestId, approvalRequestId, approvalRequestQuery.data, isEdit]);

  const updateCustomProductMenuPosition = useCallback(() => {
    const trigger = customProductMenuButtonRef.current;
    if (trigger == null) {
      setCustomProductMenuBox(null);
      return;
    }
    setCustomProductMenuBox(
      computeHorizontalMenuPanel({
        anchorRect: trigger.getBoundingClientRect(),
        menuWidth: 220,
        horizontalAlign: "end",
        maxHeightCap: 160,
      }),
    );
  }, []);

  useEffect(() => {
    if (!customProductMenuOpen) return;
    updateCustomProductMenuPosition();
    const onScrollOrResize = () => updateCustomProductMenuPosition();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [customProductMenuOpen, updateCustomProductMenuPosition]);

  useEffect(() => {
    if (!customProductMenuOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (customProductMenuButtonRef.current?.contains(target)) return;
      setCustomProductMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [customProductMenuOpen]);

  const lineTotals = useMemo(
    () => lines.map((line) => multiplyDecimalStrings(line.quantity, line.unitPrice) ?? "0"),
    [lines],
  );
  const draftTotal = useMemo(() => addDecimalStrings(lineTotals) ?? "0", [lineTotals]);
  const quoteComments = commentsManuallyEdited ? comments : defaultQuoteComments(lines);
  const clientOptions = clientsQuery.data?.options ?? [];
  const endUserOptions = endUsersQuery.data?.options ?? [];
  const skuOptions = skusQuery.data?.options ?? [];
  const selectedClientLabel = clientOptions.find((option) => option.value === clientId)?.label ?? null;
  const selectedEndUserLabel = endUserOptions.find((option) => option.value === endUserId)?.label ?? null;
  const selectedProductSkuLabel = skuOptions.find((option) => option.value === productSku)?.label ?? null;
  const visibleProductTiers = productTiers
    .map((tier, index) => ({ tier, index }))
    .filter(({ tier }) => tier.pricingType === productPricingType);
  const validThroughDays = daysUntilDate(validThrough);
  const quoteSettings = quoteSettingsQuery.data ?? { priceLimitMin: 0.95, priceLimitMax: 1.05, amountLimit: 50_000, talkLimit: 15_000 };
  const thresholdWarning = decimalStringGreaterThan(draftTotal, String(quoteSettings.talkLimit));
  const downloadBlocked = quoteRestricted
    && Number(quoteQuery.data?.total ?? draftTotal) > quoteSettings.amountLimit
    && String(quoteQuery.data?.status ?? "").toLowerCase() !== "approved";
  const otherQuotedItems = (endUserQuotesQuery.data?.items ?? []).filter((item) => String(item.quoteId) !== quoteId);
  const hasBlockingError = lines.some((line) => {
    if (line.blockingError) return true;
    if (!quoteRestricted) return false;
    const baseline = editingLineId === line.localId ? editingLineSnapshot : line;
    const issues = restrictedQuoteLineIssues(line, quoteSettings, baseline);
    return Boolean(issues.quantityError || issues.priceError);
  });
  const hasMixedPricingTypes = useMemo(() => {
    const pricingTypes = new Set(
      lines
        .map((line) => line.pricingType.trim().toLowerCase())
        .filter((type) => type !== "" && type !== "custom"),
    );
    return pricingTypes.size > 1;
  }, [lines]);
  const quoteDirty =
    !isEdit ||
    (quoteBaseline != null &&
      formSnapshotDirty(
        quoteEditorPersistSnapshot({
          clientId,
          endUserId,
          validThrough,
          paymentTerm,
          comments: quoteComments,
          lines,
        }),
        quoteBaseline,
      ));
  const copyBlockReason = quoteRestricted
    ? restrictedQuoteCopyBlockReason(quoteQuery.data?.lines ?? [], quoteSettings)
    : null;
  const pdfActionsDisabled = downloadBlocked || quoteDirty;
  function pdfActionTitle(action: "download" | "print"): string | undefined {
    if (quoteDirty) return `Save the quote before you ${action}.`;
    if (downloadBlocked) {
      return `Quotes over $${quoteSettings.amountLimit.toLocaleString()} require approval before ${action}.`;
    }
    return undefined;
  }

  const pricingMutation = useMutation({
    mutationFn: ({ index, sku }: { index: number; sku: string }) =>
      fetchQuotePricing(sku).then((pricing) => ({ index, pricing })),
    onSuccess: ({ index, pricing }) => {
      const tiers = quoteProductPricingTiers(pricing, { includeSyntheticOpenEnded: quotePrivileged });
      setLines((current) => current.map((line, lineIndex) => {
        if (lineIndex !== index) return line;
        const quantity = Number(line.quantity);
        const preferredType = line.pricingType || (tiers.some((tier) => tier.pricingType === "standard") ? "standard" : tiers[0]?.pricingType ?? "");
        const tier = tiers.find((candidate) => {
          if (candidate.pricingType !== preferredType || !candidate.price) return false;
          const minimum = candidate.minimumQuantity == null ? null : Number(candidate.minimumQuantity);
          const maximum = candidate.maximumQuantity == null ? null : Number(candidate.maximumQuantity);
          return (minimum == null || quantity >= minimum) && (maximum == null || quantity <= maximum);
        });
        const sameTier = (line.minimumQuantity ?? null) === (tier?.minimumQuantity ?? null)
          && (line.maximumQuantity ?? null) === (tier?.maximumQuantity ?? null);
        return {
          ...line,
          description: pricing.description ?? line.description,
          unitPrice: quoteRestricted && sameTier && line.unitPrice ? line.unitPrice : (tier?.price ?? ""),
          originalPrice: tier?.price ?? line.originalPrice,
          pricingType: preferredType,
          minimumQuantity: tier?.minimumQuantity ?? null,
          maximumQuantity: tier?.maximumQuantity ?? null,
          warnings: pricing.warnings ?? [],
          blockingError: tier == null ? `No ${preferredType || "selected"} pricing tier covers quantity ${line.quantity}.` : pricing.blockingError ?? null,
        };
      }));
    },
    onError: (error) => showToast({
      message: error instanceof Error ? error.message : "Could not load SKU pricing.",
      tone: "error",
    }),
  });
  const productPricingMutation = useMutation({
    mutationFn: fetchQuotePricing,
    onSuccess: (pricing) => {
      const tiers = quoteProductPricingTiers(pricing, { includeSyntheticOpenEnded: quotePrivileged });
      const pricingType = tiers.some((tier) => tier.pricingType === "standard")
        ? "standard"
        : tiers.find((tier) => tier.pricingType)?.pricingType ?? "standard";
      setProductDescription(pricing.description ?? "");
      setProductDescriptionError(pricing.descriptionError ?? "");
      setProductTiers(tiers);
      setPricingDiagnostics(pricing.diagnostics);
      setProductPricingType(pricingType);
      setProductQuantities({});
      setProductPrices(Object.fromEntries(tiers.map((tier, index) => [index, tier.price])));
    },
    onError: (error) => {
      setProductDescription("");
      setProductDescriptionError(error instanceof Error ? error.message : "Could not generate product description.");
      setProductTiers([]);
      setPricingDiagnostics(undefined);
      showToast({
        message: error instanceof Error ? error.message : "Could not load SKU pricing.",
        tone: "error",
      });
    },
  });

  function writeInput(): QuoteWriteInput {
    return {
      clientId: clientId || null,
      endUserId: endUserId || null,
      validThrough: validThrough || null,
      paymentTerm: paymentTerm.trim(),
      comments: quoteComments.trim(),
      lines: lines.map(({ localId: _localId, warnings: _warnings, blockingError: _blockingError, id: _id, total: _total, ...line }) => ({
        ...line,
        sku: line.sku.trim(),
        description: line.description.trim(),
        quantity: line.quantity.trim(),
        unitPrice: line.unitPrice.trim(),
      })),
    };
  }

  const saveMutation = useMutation({
    mutationFn: () => isEdit && quoteId ? updateQuote(quoteId, writeInput()) : createQuote(writeInput()),
    onSuccess: async (quote) => {
      if (approvalRequestId) {
        await linkInsideSalesApproval(approvalRequestId, { quoteId: quote.id });
        await queryClient.invalidateQueries({ queryKey: ["inside-sales", "approvals"] });
      }
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_QUOTES_QUERY_KEY });
      showToast({ message: "Quote draft saved.", tone: "success" });
      setQuoteBaseline(
        quoteEditorPersistSnapshot({
          clientId,
          endUserId,
          validThrough,
          paymentTerm,
          comments: quoteComments,
          lines,
        }),
      );
      setQuoteRestore({
        clientId,
        endUserId,
        validThrough,
        paymentTerm,
        comments: quoteComments,
        commentsManuallyEdited,
        lines: cloneDraftLines(lines),
      });
      if (!isEdit) navigate(`/inside-sales/quotes/${encodeURIComponent(quote.id)}`, { replace: true });
    },
    onError: (error) => showToast({
      message: error instanceof Error ? error.message : "Could not save quote.",
      tone: "error",
    }),
  });
  const duplicateMutation = useMutation({
    mutationFn: () => duplicateQuote(quoteId ?? ""),
    onSuccess: async (quote) => {
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_QUOTES_QUERY_KEY });
      navigate(`/inside-sales/quotes/${encodeURIComponent(quote.id)}`);
    },
    onError: (error) => showToast({ message: error instanceof Error ? error.message : "Could not duplicate quote.", tone: "error" }),
  });
  const deleteMutation = useMutation({
    mutationFn: () => deleteQuote(quoteId ?? ""),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: INSIDE_SALES_QUOTES_QUERY_KEY });
      navigate("/inside-sales/quotes");
    },
    onError: (error) => showToast({ message: error instanceof Error ? error.message : "Could not delete quote.", tone: "error" }),
  });

  function updateLine(index: number, patch: Partial<DraftLine>) {
    setLines((current) => current.map((line, lineIndex) => lineIndex === index ? { ...line, ...patch } : line));
  }

  function beginLineEdit(line: DraftLine) {
    setEditingLineSnapshot({ ...line });
    setEditingLineId(line.localId);
  }

  function finishLineEdit() {
    setEditingLineId(null);
    setEditingLineSnapshot(null);
  }

  function cancelLineEdit(index: number) {
    if (editingLineSnapshot != null) {
      setLines((current) => current.map((line, lineIndex) => (
        lineIndex === index && line.localId === editingLineSnapshot.localId
          ? { ...editingLineSnapshot }
          : line
      )));
    }
    finishLineEdit();
  }

  function addProductFromTier(tier: QuoteProductPricingTier, tierIndex: number) {
    const quantity = productQuantities[tierIndex]?.trim() ?? "";
    const price = productPrices[tierIndex]?.trim() ?? "";
    const parsedQuantity = Number(quantity);
    const parsedPrice = Number(price);
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0 || !Number.isInteger(parsedQuantity)) {
      showToast({ message: "Enter a whole-number quantity greater than zero.", tone: "error" });
      return;
    }
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      showToast({ message: "Enter a valid price.", tone: "error" });
      return;
    }
    const minimum = tier.minimumQuantity == null ? null : Number(tier.minimumQuantity);
    const maximum = tier.maximumQuantity == null ? null : Number(tier.maximumQuantity);
    if ((minimum != null && parsedQuantity < minimum) || (maximum != null && parsedQuantity > maximum)) {
      showToast({ message: `Quantity must be within ${quoteProductTierRange(tier)}.`, tone: "error" });
      return;
    }
    setLines((current) => [...current, {
      ...emptyLine(),
      sku: productSku,
      description: productDescription,
      quantity,
      unitPrice: price,
      pricingType: tier.pricingType,
      originalPrice: tier.price,
      minimumQuantity: tier.minimumQuantity,
      maximumQuantity: tier.maximumQuantity,
    }]);
    setProductQuantities((current) => ({ ...current, [tierIndex]: "" }));
    showToast({ message: `${productSku} added`, tone: "success" });
  }

  function addCustomProduct() {
    const quantity = Number(customQuantity);
    const price = Number(customPrice);
    if (!customSku.trim() || !customDescription.trim()) {
      showToast({ message: "Enter a custom SKU and description.", tone: "error" });
      return;
    }
    if (!Number.isInteger(quantity) || quantity <= 0 || !Number.isFinite(price) || price < 0) {
      showToast({ message: "Enter a whole-number quantity and a valid price.", tone: "error" });
      return;
    }
    setLines((current) => [...current, {
      ...emptyLine(),
      sku: customSku.trim(),
      description: customDescription.trim(),
      quantity: customQuantity,
      unitPrice: customPrice,
      pricingType: "custom",
      originalPrice: customPrice,
    }]);
    showToast({ message: `${customSku.trim()} added`, tone: "success" });
    setCustomSku("");
    setCustomDescription("");
    setCustomQuantity("");
    setCustomPrice("");
    setCustomProductOpen(false);
  }

  function resetQuoteDraft() {
    if (quoteRestore == null) return;
    setClientId(quoteRestore.clientId);
    setClientSearch("");
    setEndUserId(quoteRestore.endUserId);
    setEndUserSearch("");
    setValidThrough(quoteRestore.validThrough);
    setPaymentTerm(quoteRestore.paymentTerm);
    setComments(quoteRestore.comments);
    setCommentsManuallyEdited(quoteRestore.commentsManuallyEdited);
    setLines(cloneDraftLines(quoteRestore.lines));
    finishLineEdit();
  }

  function cancelCustomProduct() {
    setCustomSku("");
    setCustomDescription("");
    setCustomQuantity("");
    setCustomPrice("");
    setCustomProductOpen(false);
  }

  if (quoteQuery.isLoading) return <Card><CardBody className="py-6 text-sm text-slate-600">Loading quote...</CardBody></Card>;
  if (quoteQuery.isError) return <Card><CardBody className="py-6 text-sm text-rose-700">{quoteQuery.error.message}</CardBody></Card>;

  const quote = quoteQuery.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <HubPageTitle breadcrumbLabel={quote?.number || "New quote"}>
          {quote?.number || (isEdit ? "Quote" : "New quote")}
        </HubPageTitle>
        <div className="flex flex-wrap gap-2">
          {isEdit ? (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={copyBlockReason != null}
                title={copyBlockReason ?? undefined}
                onClick={() => duplicateMutation.mutate()}
                isLoading={duplicateMutation.isPending}
              >
                Duplicate
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={pdfActionsDisabled}
                title={pdfActionTitle("download")}
                onClick={() => {
                  void downloadQuotePdf(quoteId ?? "", `${quote?.number || "quote"}.pdf`).catch((error) => {
                    showToast({
                      message: error instanceof Error ? error.message : "Could not generate the quote PDF.",
                      tone: "error",
                    });
                  });
                }}
              >
                <Download className="h-4 w-4" aria-hidden /> PDF
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={pdfActionsDisabled}
                title={pdfActionTitle("print")}
                onClick={() => {
                  void printQuotePdf(quoteId ?? "").catch((error) => {
                    showToast({
                      message: error instanceof Error ? error.message : "Could not print the quote PDF.",
                      tone: "error",
                    });
                  });
                }}
              >
                <Printer className="h-4 w-4" aria-hidden /> Print
              </Button>
              <Button
                type="button"
                variant="danger"
                onClick={() => {
                  if (window.confirm("Delete this quote draft?")) deleteMutation.mutate();
                }}
                isLoading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </>
          ) : null}
          {isEdit ? (
            <Button type="button" variant="secondary" disabled={!quoteDirty || quoteRestore == null} onClick={resetQuoteDraft}>
              Reset
            </Button>
          ) : null}
          <SaveChangesButton
            type="button"
            isDirty={quoteDirty}
            onClick={() => saveMutation.mutate()}
            isLoading={saveMutation.isPending}
            disabled={hasBlockingError || hasMixedPricingTypes || lines.length === 0}
          >
            Save draft
          </SaveChangesButton>
        </div>
      </div>

      {isEdit && quoteDirty ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-950">
          Unsaved changes
        </div>
      ) : null}

      {quote ? (
        <div className="flex flex-wrap gap-2 text-xs text-slate-600">
          <span className="rounded-full bg-slate-100 px-2 py-1 capitalize">{quote.status.replaceAll("_", " ")}</span>
          <span>Created by {quote.createdByDisplayName || "Unknown"}</span>
        </div>
      ) : null}

      <Card>
        <CardHeader><CardTitle>Quote details</CardTitle></CardHeader>
        <CardBody className="grid grid-cols-2 gap-3 xl:grid-cols-5">
          <div>
            <Label htmlFor="quote-client">Client</Label>
            <SearchableSelect
              id="quote-client"
              value={clientId}
              onValueChange={(option) => {
                setClientId(option?.value ?? "");
                setClientSearch("");
              }}
              options={clientOptions}
              searchValue={clientSearch}
              onSearchChange={setClientSearch}
              placeholder="Select client"
              searchPlaceholder="Search clients"
              emptyMessage={clientsQuery.isLoading ? "Loading clients..." : "No clients found."}
              isLoading={clientsQuery.isLoading}
              selectedLabelOverride={selectedClientLabel}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="quote-end-user">End user</Label>
            <SearchableSelect
              id="quote-end-user"
              value={endUserId}
              onValueChange={(option) => {
                setEndUserId(option?.value ?? "");
                setEndUserSearch("");
              }}
              options={endUserOptions}
              searchValue={endUserSearch}
              onSearchChange={setEndUserSearch}
              placeholder="Select end user"
              searchPlaceholder="Search end users"
              emptyMessage={endUsersQuery.isLoading ? "Loading end users..." : "No end users found."}
              isLoading={endUsersQuery.isLoading}
              selectedLabelOverride={selectedEndUserLabel}
              className="mt-1"
            />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <Label htmlFor="quote-valid-through">Valid through</Label>
              <HoverHintWithGlyph
                hoverHint={
                  validThroughDays == null
                    ? "Choose a valid-through date."
                    : validThroughDays === 0
                      ? "This quote expires today."
                      : validThroughDays > 0
                        ? `${validThroughDays} day${validThroughDays === 1 ? "" : "s"} until this quote expires.`
                        : `This date was ${Math.abs(validThroughDays)} day${validThroughDays === -1 ? "" : "s"} ago.`
                }
              />
            </div>
            <Input id="quote-valid-through" type="date" value={validThrough} onChange={(event) => setValidThrough(event.target.value)} className="mt-1" />
          </div>
          <div>
            <Label htmlFor="quote-payment-term">Payment term</Label>
            <select
              id="quote-payment-term"
              value={paymentTerm}
              onChange={(event) => setPaymentTerm(event.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm"
            >
              <option value="">Select payment term</option>
              {QUOTE_PAYMENT_TERMS.map((term) => <option key={term} value={term}>{term}</option>)}
            </select>
          </div>
          <div className="col-span-2 xl:col-span-1">
            <Label htmlFor="quote-comments">Comments</Label>
            <TextArea
              id="quote-comments"
              rows={1}
              value={quoteComments}
              onChange={(event) => {
                setComments(event.target.value);
                setCommentsManuallyEdited(true);
              }}
              className="mt-1 h-10 min-h-10 resize-y py-2"
            />
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-4 xl:grid-cols-[max-content_minmax(22rem,1fr)_minmax(24rem,1fr)] xl:items-start">
      <Card className="order-2 flex min-h-0 w-full max-w-full flex-col overflow-hidden xl:order-1 xl:w-max">
        <CardHeader className="px-3 py-3"><CardTitle>Quote lines</CardTitle></CardHeader>
        <div className="overflow-auto">
          <table className="w-max table-fixed border-collapse text-sm">
            <colgroup>
              <col className="w-28" />
              <col className="w-72" />
              <col className="w-14" />
              <col className="w-20" />
              <col className="w-20" />
              <col className="w-28" />
            </colgroup>
            <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95">
              <tr>
                <th className="px-2 py-1.5 text-left">SKU</th>
                <th className="px-2 py-1.5 text-left">Description</th>
                <th className="px-1.5 py-1.5 text-right">Qty</th>
                <th className="px-2 py-1.5 text-right">Price</th>
                <th className="px-2 py-1.5 text-right">Total</th>
                <th className="px-1.5 py-1.5 text-right"><span className="sr-only">Action</span></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => {
                const editing = editingLineId === line.localId;
                const issues = quoteRestricted
                  ? restrictedQuoteLineIssues(
                    line,
                    quoteSettings,
                    editing ? editingLineSnapshot : line,
                  )
                  : { quantityError: null, priceError: null, priceWarning: null, quantityWarning: null, priceBounds: null };
                const lineBlocked = Boolean(issues.quantityError || issues.priceError);
                return (
                  <tr key={line.localId} className="border-b border-slate-100 align-top hover:[&>td]:bg-slate-50/80">
                    <td className="w-28 px-2 py-1.5">
                      {editing ? (
                        <>
                          <Input
                            list="quote-sku-options"
                            value={line.sku}
                            onChange={(event) => updateLine(index, { sku: event.target.value })}
                            onBlur={() => {
                              if (line.sku.trim()) pricingMutation.mutate({ index, sku: line.sku.trim() });
                            }}
                            className="h-7 px-1.5 text-xs"
                          />
                          {line.warnings.map((warning) => <div key={warning} className="mt-1 text-xs text-amber-700">{warning}</div>)}
                          {line.blockingError ? <div className="mt-1 text-xs text-rose-700">{line.blockingError}</div> : null}
                        </>
                      ) : <span className="block truncate" title={line.sku}>{line.sku || "-"}</span>}
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {editing
                        ? <TextArea value={line.description} onChange={(event) => updateLine(index, { description: event.target.value })} className="min-h-16 px-1.5 text-xs" />
                        : <span className="block whitespace-normal break-words">{line.description || "-"}</span>}
                    </td>
                    <td className="w-14 px-1.5 py-1.5 text-right tabular-nums">
                      {editing
                        ? (
                            <>
                              <Input
                                value={line.quantity}
                                inputMode="numeric"
                                type="number"
                                min={line.minimumQuantity ?? "1"}
                                max={quoteRestricted && !issues.quantityWarning ? line.maximumQuantity ?? undefined : undefined}
                                aria-label="Quantity"
                                onChange={(event) => updateLine(index, { quantity: event.target.value })}
                                onBlur={() => { if (line.sku.trim()) pricingMutation.mutate({ index, sku: line.sku.trim() }); }}
                                className={cn("h-7 px-1 text-right text-xs tabular-nums", issues.quantityError && "border-rose-400")}
                              />
                              {issues.quantityError ? <p className="mt-1 text-left text-[11px] font-medium leading-4 text-rose-700">{issues.quantityError}</p> : null}
                              {!issues.quantityError && issues.quantityWarning ? <p className="mt-1 text-left text-[11px] leading-4 text-amber-700">{issues.quantityWarning}</p> : null}
                            </>
                          )
                        : (
                            <>
                              {line.quantity}
                              {issues.quantityError ? <p className="mt-1 text-left text-[11px] font-medium leading-4 text-rose-700">{issues.quantityError}</p> : null}
                              {!issues.quantityError && issues.quantityWarning ? <p className="mt-1 text-left text-[11px] leading-4 text-amber-700">{issues.quantityWarning}</p> : null}
                            </>
                          )}
                    </td>
                    <td className="w-20 px-2 py-1.5 text-right tabular-nums">
                      {editing
                        ? (
                            <>
                              <Input
                                value={line.unitPrice}
                                inputMode="decimal"
                                type="number"
                                onChange={(event) => updateLine(index, { unitPrice: event.target.value })}
                                className={cn("h-7 px-1.5 text-right text-xs tabular-nums", issues.priceError && "border-rose-400")}
                              />
                              {issues.priceError ? (
                                <p className="mt-1 text-left text-[11px] font-medium leading-4 text-rose-700">{issues.priceError}</p>
                              ) : issues.priceWarning ? (
                                <p className="mt-1 text-left text-[11px] leading-4 text-amber-700">{issues.priceWarning}</p>
                              ) : issues.priceBounds ? (
                                <p className="mt-1 text-left text-[11px] leading-4 text-slate-500">
                                  Allowed price ${issues.priceBounds.min.toFixed(2)}–${issues.priceBounds.max.toFixed(2)}
                                </p>
                              ) : null}
                            </>
                          )
                        : (
                            <>
                              {formatMoneyString(line.unitPrice)}
                              {issues.priceError ? <p className="mt-1 text-left text-[11px] font-medium leading-4 text-rose-700">{issues.priceError}</p> : null}
                              {!issues.priceError && issues.priceWarning ? <p className="mt-1 text-left text-[11px] leading-4 text-amber-700">{issues.priceWarning}</p> : null}
                            </>
                          )}
                    </td>
                    <td className="w-20 px-2 py-1.5 text-right font-medium tabular-nums">{formatMoneyString(lineTotals[index])}</td>
                    <td className="w-28 px-1 py-1.5">
                      <div className="flex justify-end gap-0.5">
                        {editing ? (
                          <>
                            <Button type="button" size="sm" variant="secondary" className="h-7 px-2" disabled={lineBlocked} onClick={finishLineEdit}>Done</Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2"
                              onClick={() => cancelLineEdit(index)}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              aria-label="Remove product"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                setLines((current) => current.filter((_, lineIndex) => lineIndex !== index));
                                finishLineEdit();
                              }}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden />
                            </Button>
                          </>
                        ) : (
                          <Button type="button" size="sm" variant="ghost" aria-label="Edit line" className="h-7 w-7 p-0" onClick={() => beginLineEdit(line)}>
                            <Pencil className="h-4 w-4" aria-hidden />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {lines.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 py-5 text-center text-sm text-slate-500">
                    No products added yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <CardBody className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200">
          <div>
            {thresholdWarning ? <p className="text-sm text-amber-700">Quote exceeds ${quoteSettings.talkLimit.toLocaleString()}. Talk to Robby or William before sending.</p> : null}
            {hasBlockingError ? <p className="text-sm text-rose-700">Resolve quantity and price restrictions before saving.</p> : null}
            {hasMixedPricingTypes ? <p className="text-sm text-rose-700">All quote lines must use the same pricing type.</p> : null}
          </div>
          <div className="text-lg font-semibold tabular-nums">{formatMoneyString(quote?.total ?? draftTotal)}</div>
        </CardBody>
      </Card>

      <Card className="order-1 min-w-0 xl:order-2">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle>Add product</CardTitle>
          {quotePrivileged ? (
            <>
              <Button
                ref={customProductMenuButtonRef}
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 w-8 shrink-0 p-0 text-slate-500 hover:text-slate-900"
                aria-label="More add product actions"
                aria-haspopup="menu"
                aria-expanded={customProductMenuOpen}
                onClick={() => setCustomProductMenuOpen((open) => !open)}
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
              {customProductMenuOpen && customProductMenuBox != null
                ? createPortal(
                    <div
                      className="fixed overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
                      style={{
                        left: customProductMenuBox.left,
                        top: customProductMenuBox.top,
                        width: customProductMenuBox.width,
                        zIndex: FLOATING_POPOVER_Z_INDEX,
                      }}
                      role="menu"
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className="block w-full px-3 py-2 text-left text-sm text-slate-900 hover:bg-slate-50"
                        onClick={() => {
                          setCustomProductOpen(true);
                          setCustomProductMenuOpen(false);
                        }}
                      >
                        Add custom product
                      </button>
                    </div>,
                    document.body,
                  )
                : null}
            </>
          ) : null}
        </CardHeader>
        <CardBody className="space-y-4">
          {customProductOpen ? (
            <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="text-xs text-amber-800">This adds a quote line only. It does not create a catalog SKU.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="custom-quote-sku">Custom SKU</Label>
                  <Input id="custom-quote-sku" value={customSku} onChange={(event) => setCustomSku(event.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="custom-quote-quantity">Quantity</Label>
                  <Input id="custom-quote-quantity" type="number" min="1" step="1" value={customQuantity} onChange={(event) => setCustomQuantity(event.target.value)} className="mt-1" />
                </div>
              </div>
              <div>
                <Label htmlFor="custom-quote-description">Description</Label>
                <TextArea id="custom-quote-description" value={customDescription} onChange={(event) => setCustomDescription(event.target.value)} className="mt-1 min-h-20" />
              </div>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label htmlFor="custom-quote-price">Unit price</Label>
                  <Input id="custom-quote-price" type="number" min="0" step="0.01" value={customPrice} onChange={(event) => setCustomPrice(event.target.value)} className="mt-1" />
                </div>
                <Button type="button" variant="secondary" onClick={cancelCustomProduct}>
                  Cancel
                </Button>
                <Button type="button" onClick={addCustomProduct} disabled={!customSku.trim() || !customDescription.trim() || !customQuantity || !customPrice}>
                  Add custom line
                </Button>
              </div>
            </div>
          ) : null}
          {!clientId || !endUserId ? (
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              Select a client and end user to add catalog products.
            </p>
          ) : (
            <Fragment>
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-1">
          <div>
            <Label htmlFor="quote-product-sku">SKU</Label>
            <SearchableSelect
              id="quote-product-sku"
              value={productSku}
              onValueChange={(option) => {
                const sku = option?.value ?? "";
                setProductSku(sku);
                setProductSkuSearch("");
                setProductDescription("");
                setProductDescriptionError("");
                setProductPricingType("standard");
                setProductTiers([]);
                setPricingDiagnostics(undefined);
                setPricingLogOpen(false);
                setProductQuantities({});
                setProductPrices({});
                if (sku) productPricingMutation.mutate(sku);
              }}
              options={skuOptions}
              searchValue={productSkuSearch}
              onSearchChange={setProductSkuSearch}
              placeholder="Select part number"
              searchPlaceholder="Search SKU or description"
              emptyMessage={skusQuery.isLoading ? "Loading products..." : "No products found."}
              isLoading={skusQuery.isLoading || productPricingMutation.isPending}
              selectedLabelOverride={selectedProductSkuLabel}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="quote-product-client-type">Client type</Label>
            <select
              id="quote-product-client-type"
              value={productPricingType}
              onChange={(event) => setProductPricingType(event.target.value)}
              disabled={!productSku}
              className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:bg-slate-100 disabled:text-slate-400"
            >
              {QUOTE_CLIENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </div>
          </div>

          <div>
            <Label>Description</Label>
            <TextArea
              value={productDescription}
              onChange={(event) => setProductDescription(event.target.value)}
              placeholder="Generated product description"
              className="mt-1 min-h-20"
            />
            {productDescriptionError ? (
              <p className="mt-1 text-xs text-rose-700">Could not generate the description: {productDescriptionError}</p>
            ) : null}
          </div>

          <div>
            <div className="grid grid-cols-[minmax(4.5rem,1fr)_4.5rem_5.5rem_auto] gap-2 border-b border-slate-200 pb-2 text-xs font-medium text-slate-600">
              <span>Range</span>
              <span className="text-right">Qty</span>
              <span className="text-right">Price</span>
              <span className="w-14 text-right">Add</span>
            </div>
            <div className="divide-y divide-slate-100">
              {visibleProductTiers.map(({ tier, index }) => {
                const quantity = Number(productQuantities[index]);
                const minimum = tier.minimumQuantity == null ? null : Number(tier.minimumQuantity);
                const maximum = tier.maximumQuantity == null ? null : Number(tier.maximumQuantity);
                const quantityValid = Number.isInteger(quantity)
                  && quantity > 0
                  && (minimum == null || quantity >= minimum)
                  && (maximum == null || quantity <= maximum);
                const basePrice = Number(tier.price);
                const minimumPrice = basePrice * quoteSettings.priceLimitMin;
                const maximumPrice = basePrice * quoteSettings.priceLimitMax;
                const enteredPrice = Number(productPrices[index] ?? tier.price);
                const priceValid = quotePrivileged || (
                  Number.isFinite(enteredPrice)
                  && enteredPrice >= minimumPrice - 0.000001
                  && enteredPrice <= maximumPrice + 0.000001
                );
                return (
                <div key={`${tier.pricingType}-${index}`} className="py-2">
                <div className="grid grid-cols-[minmax(4.5rem,1fr)_4.5rem_5.5rem_auto] items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">
                    {quoteProductTierRange(tier)}
                  </span>
                  <Input
                    aria-label={`Quantity for ${quoteProductTierRange(tier)}`}
                    value={productQuantities[index] ?? ""}
                    inputMode="numeric"
                    type="number"
                    min={tier.minimumQuantity ?? "1"}
                    max={tier.maximumQuantity ?? undefined}
                    onChange={(event) => setProductQuantities((current) => ({ ...current, [index]: event.target.value }))}
                    className={cn("h-8 px-2 text-right tabular-nums", productQuantities[index] && !quantityValid && "border-rose-400")}
                  />
                  <Input
                    aria-label={`Price for ${quoteProductTierRange(tier)}`}
                    value={productPrices[index] ?? tier.price}
                    inputMode="decimal"
                    type="number"
                    min={quotePrivileged ? "0" : minimumPrice.toFixed(2)}
                    max={quotePrivileged ? undefined : maximumPrice.toFixed(2)}
                    onChange={(event) => setProductPrices((current) => ({ ...current, [index]: event.target.value }))}
                    title={quotePrivileged ? "Catalog price limits do not apply to this role." : `Allowed: ${minimumPrice.toFixed(2)}–${maximumPrice.toFixed(2)}`}
                    className={cn("h-8 px-2 text-right tabular-nums", !priceValid && "border-rose-400")}
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => addProductFromTier(tier, index)}
                    disabled={!quantityValid || !priceValid || !(productPrices[index] ?? tier.price).trim() || !productDescription.trim()}
                    className="w-14 shrink-0 border border-orange-600 bg-orange-500 px-2 text-white hover:bg-orange-600 disabled:border-slate-300 disabled:bg-slate-100 disabled:text-slate-400"
                  >
                    Add
                  </Button>
                </div>
                {quoteRestricted && Number.isFinite(basePrice) && basePrice > 0 ? (
                  <p className={cn("mt-1 text-[11px] leading-4", priceValid ? "text-slate-500" : "font-medium text-rose-700")}>
                    Allowed price ${minimumPrice.toFixed(2)}–${maximumPrice.toFixed(2)}
                    {priceValid ? "" : ` · ${enteredPrice.toFixed(2)} is outside this range`}
                  </p>
                ) : null}
                </div>
              )})}
              {productSku && !productPricingMutation.isPending && visibleProductTiers.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-500">No pricing ranges are available.</p>
              ) : null}
              {!productSku ? (
                <p className="py-4 text-center text-sm text-slate-500">Select a SKU to view pricing.</p>
              ) : null}
            </div>
          </div>
          {quotePrivileged && productSku && pricingDiagnostics ? (
            <div className="border-t border-slate-200 pt-3">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                aria-expanded={pricingLogOpen}
                onClick={() => setPricingLogOpen((current) => !current)}
              >
                {pricingLogOpen ? "Hide pricing log" : "Open pricing log"}
              </Button>
              {pricingLogOpen ? (
                <div className="mt-3 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="rounded bg-white p-2"><div className="text-lg font-semibold">{pricingDiagnostics.rawPriceRowCount}</div><div className="text-xs text-slate-500">Template rows</div></div>
                    <div className="rounded bg-white p-2"><div className="text-lg font-semibold">{pricingDiagnostics.matchingPriceRowCount}</div><div className="text-xs text-slate-500">Rules matched</div></div>
                    <div className="rounded bg-white p-2"><div className="text-lg font-semibold">{pricingDiagnostics.normalizedTierCount}</div><div className="text-xs text-slate-500">Price tiers</div></div>
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase text-slate-500">Parsed SKU</div>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(pricingDiagnostics.parsedSku).map(([key, value]) => (
                        <span key={key} className="rounded bg-white px-2 py-1 text-xs text-slate-700">
                          <strong>{key}:</strong> {Array.isArray(value) ? value.join(", ") : String(value)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <details>
                    <summary className="cursor-pointer font-medium text-slate-700">Raw rule details</summary>
                    <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-slate-950 p-3 text-xs text-slate-100">
                      {JSON.stringify(pricingDiagnostics.rawPriceRows, null, 2)}
                    </pre>
                  </details>
                </div>
              ) : null}
            </div>
          ) : null}
            </Fragment>
          )}
        </CardBody>
      </Card>

      <Card className="order-3 min-w-0 overflow-hidden">
        <CardHeader className="space-y-3">
          <CardTitle>End user history</CardTitle>
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-1">
            <button
              type="button"
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium",
                endUserHistoryTab === "quotes" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-900",
              )}
              aria-pressed={endUserHistoryTab === "quotes"}
              onClick={() => setEndUserHistoryTab("quotes")}
            >
              Quotes
            </button>
            <button
              type="button"
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium",
                endUserHistoryTab === "orders" ? "bg-white text-slate-950 shadow-sm" : "text-slate-600 hover:text-slate-900",
              )}
              aria-pressed={endUserHistoryTab === "orders"}
              onClick={() => setEndUserHistoryTab("orders")}
            >
              Sales orders
            </button>
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          {!endUserId ? (
            <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
              Select an end user to see previous quotes and sales orders.
            </p>
          ) : endUserHistoryTab === "quotes" && endUserQuotesQuery.isLoading ? (
            <p className="px-1 py-6 text-sm text-slate-500">Loading previous quotes...</p>
          ) : endUserHistoryTab === "quotes" && endUserQuotesQuery.isError ? (
            <p className="px-1 py-6 text-sm text-rose-700">{endUserQuotesQuery.error.message}</p>
          ) : endUserHistoryTab === "quotes" && otherQuotedItems.length === 0 ? (
            <p className="px-1 py-6 text-sm text-slate-500">No other quoted items for this end user.</p>
          ) : endUserHistoryTab === "quotes" ? (
            <div className="max-h-[32rem] overflow-auto rounded-md border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                  <tr>
                    <th className="whitespace-nowrap px-3 py-2">Quote</th>
                    <th className="whitespace-nowrap px-3 py-2">Valid through</th>
                    <th className="px-3 py-2">SKU</th>
                    <th className="whitespace-nowrap px-3 py-2 text-right">Unit price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {otherQuotedItems.map((item, index) => {
                    const skuMatchesSelection = productSku.trim() !== ""
                      && item.sku.trim().toLowerCase() === productSku.trim().toLowerCase();
                    return (
                    <tr
                      key={`${item.quoteId}-${item.sku}-${index}`}
                      className={skuMatchesSelection ? "bg-amber-50" : undefined}
                    >
                      <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-900">{item.quoteNumber}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-600">{formatHistoryDate(item.validThrough)}</td>
                      <td className={cn("px-3 py-2", skuMatchesSelection ? "font-medium text-slate-900" : "text-slate-700")}>{item.sku}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatMoneyString(String(item.unitPrice))}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : endUserOrdersQuery.isLoading ? (
            <p className="px-1 py-6 text-sm text-slate-500">Loading sales orders...</p>
          ) : endUserOrdersQuery.isError ? (
            <p className="px-1 py-6 text-sm text-rose-700">{endUserOrdersQuery.error.message}</p>
          ) : (endUserOrdersQuery.data?.items.length ?? 0) === 0 ? (
            <p className="px-1 py-6 text-sm text-slate-500">No sales order items for this end user.</p>
          ) : (
            <div className="max-h-[32rem] overflow-auto rounded-md border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Sales order</th>
                    <th className="px-3 py-2">Date</th>
                    <th className="px-3 py-2">SKU</th>
                    <th className="px-3 py-2 text-right">Unit price</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(endUserOrdersQuery.data?.items ?? []).map((item, index) => {
                    const skuMatchesSelection = productSku.trim() !== ""
                      && item.sku.trim().toLowerCase() === productSku.trim().toLowerCase();
                    return (
                    <tr
                      key={`${item.salesOrderId}-${item.sku}-${index}`}
                      className={skuMatchesSelection ? "bg-amber-50" : undefined}
                    >
                      <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-900">{item.salesOrderNumber}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-600">{formatHistoryDate(item.submitted)}</td>
                      <td className={cn("px-3 py-2", skuMatchesSelection ? "font-medium text-slate-900" : "text-slate-700")}>{item.sku}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatMoneyString(String(item.unitPrice))}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      </div>
      <datalist id="quote-sku-options">
        {(skusQuery.data?.options ?? []).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </datalist>
    </div>
  );
}
